import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children?: ReactNode;
    /** Change this to clear a previous error, e.g. on navigation. */
    resetKey?: string;
}

interface State {
    hasError: boolean;
}

// A release replaces the hashed chunk files, so a tab left open across a deploy
// cannot lazy load the next route. The static files are served no-store, so
// there is no cached copy either, and the SPA fallback answers with index.html
// instead of a 404 - hence the MIME type variant.
const CHUNK_ERROR_PATTERNS = [
    'failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'importing a module script failed',
    'expected a javascript',
];

const RELOAD_ATTEMPTED = 'blk.chunkReloadAttempted';

export function isChunkLoadError(error: Error) {
    const message = (error?.message ?? '').toLowerCase();
    return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
    };

    public static getDerivedStateFromError(e: Error): State {
        // Update state so the next render will show the fallback UI.
        console.log('Error', e);
        return { hasError: true };
    }

    public componentDidMount() {
        // We rendered, so a later chunk failure is allowed to reload again.
        sessionStorage.removeItem(RELOAD_ATTEMPTED);
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);

        // One reload picks up the current index.html. The flag stops a loop if
        // the chunk is genuinely gone.
        if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_ATTEMPTED)) {
            sessionStorage.setItem(RELOAD_ATTEMPTED, '1');
            window.location.reload();
        }
    }

    public componentDidUpdate(prevProps: Props) {
        if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ hasError: false });
        }
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <h1>Sorry.. there was an error</h1>
                    <button type="button" onClick={() => window.location.reload()}>
                        Reload the page
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
